"use client";
import React, { useState, useEffect } from 'react';

const QUESTIONS = [
  {
    question: "What year was the original iPhone released?",
    options: ["2005", "2007", "2009", "2010"],
    answer: 1
  },
  {
    question: "Which company developed the Unreal Engine?",
    options: ["Electronic Arts", "Unity Technologies", "Epic Games", "Ubisoft"],
    answer: 2
  },
  {
    question: "What does HTTP stand for?",
    options: ["HyperText Transfer Protocol", "HyperText Transmission Process", "Hyperlink Transfer Technology", "Hyperlink Text Transfer"],
    answer: 0
  },
  {
    question: "What is the best-selling video game console of all time?",
    options: ["Nintendo DS", "PlayStation 2", "Nintendo Switch", "PlayStation 4"],
    answer: 1
  },
  {
    question: "Who is known as the father of modern computer science?",
    options: ["Bill Gates", "Alan Turing", "Steve Jobs", "Charles Babbage"],
    answer: 1
  }
];

const TechTrivia = () => {
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  
  const question = QUESTIONS[currentQuestionIdx];

  useEffect(() => {
    if (gameOver || selectedOption !== null) return;
    
    if (timeLeft === 0) {
      handleAnswer(-1); // Timeout
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, gameOver, selectedOption]);

  const handleAnswer = (idx) => {
    if (selectedOption !== null) return;
    setSelectedOption(idx);
    
    if (idx === question.answer) {
      setScore(prev => prev + 100 + (timeLeft * 10)); // Bonus points for time
    }

    setTimeout(() => {
      if (currentQuestionIdx < QUESTIONS.length - 1) {
        setCurrentQuestionIdx(prev => prev + 1);
        setSelectedOption(null);
        setTimeLeft(15);
      } else {
        setGameOver(true);
      }
    }, 1500);
  };

  const resetGame = () => {
    setCurrentQuestionIdx(0);
    setScore(0);
    setGameOver(false);
    setSelectedOption(null);
    setTimeLeft(15);
  };

  return (
    <div className="w-full bg-brand-surface border border-gray-300 rounded-lg p-6 text-brand-text shadow-sm relative overflow-hidden">
      
      {!gameOver ? (
        <div className="relative z-10">
          <div className="flex justify-between items-center mb-6 border-b border-brand-border pb-4">
            <h2 className="text-xl font-black uppercase tracking-tight text-brand-text">
              Tech Trivia
            </h2>
            <div className="text-sm font-bold text-brand-muted">
              Score: <span className="text-brand-text">{score}</span>
            </div>
          </div>

          <div className="mb-4 flex justify-between text-xs font-bold text-brand-muted uppercase tracking-widest">
            <span>Question {currentQuestionIdx + 1} of {QUESTIONS.length}</span>
            <span className={timeLeft <= 5 ? 'text-brand-text animate-pulse' : 'text-brand-muted'}>{timeLeft}s</span>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-1 bg-gray-200 rounded-full mb-6 overflow-hidden">
            <div 
              className={`h-full transition-all duration-1000 ease-linear ${timeLeft <= 5 ? 'bg-brand-secondary' : 'bg-gray-400'}`} 
              style={{ width: `${(timeLeft / 15) * 100}%` }}
            ></div>
          </div>

          <h3 className="text-xl md:text-2xl font-bold leading-snug mb-8 text-brand-text min-h-[4rem]">
            {question.question}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {question.options.map((opt, idx) => {
              let btnClass = "border-gray-300 bg-brand-surface hover:bg-[var(--brand-surface-hover)] hover:border-[var(--brand-border-hover)] text-gray-700";
              if (selectedOption !== null) {
                if (idx === question.answer) {
                  btnClass = "border-gray-900 bg-brand-surface-alt text-brand-text font-bold";
                } else if (idx === selectedOption) {
                  btnClass = "border-gray-400 bg-brand-surface-alt text-brand-muted line-through";
                } else {
                  btnClass = "border-brand-border bg-brand-surface text-brand-muted opacity-50";
                }
              }

              return (
                <button
                  key={idx}
                  disabled={selectedOption !== null}
                  onClick={() => handleAnswer(idx)}
                  className={`w-full text-left p-4 rounded border transition-all duration-200 font-medium ${btnClass}`}
                >
                  <span className="text-xs text-brand-muted mr-3 block md:inline mb-1 md:mb-0">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-surface-alt rounded-full mb-6">
            <svg className="w-10 h-10 text-brand-text" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          </div>
          <h2 className="text-3xl font-black mb-2 text-brand-text">Quiz Complete!</h2>
          <p className="text-brand-muted mb-8">You scored <span className="text-brand-text font-bold text-xl">{score}</span> points.</p>
          <button 
            onClick={resetGame}
            className="px-8 py-3 bg-brand-secondary text-brand-text-inverse font-bold rounded-full hover:bg-[var(--brand-surface-hover)] transition-colors"
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default TechTrivia;
